import { bytesToBase64, decodePcm16Base64, floatToPcm16, resampleLinear } from './protocol';
import type { RealtimeClientCallbacks, RealtimeConnectionState } from './realtimeClient';

// =============================================================================
// ElevenLabs Conversational-AI realtime client (TRUE speech-to-speech).
//
// Connects to a short-lived signed WS URL minted server-side by nexus-core
// (POST /api/nexus/voice/realtime/session) — the ElevenLabs API key never
// reaches the browser. Wire protocol (JSON frames over the signed WS):
//   server → { type: 'conversation_initiation_metadata', ... audio formats }
//           { type: 'audio', audio_event: { audio_base_64, event_id } }
//           { type: 'user_transcript', user_transcription_event: { user_transcript } }
//           { type: 'agent_response', agent_response_event: { agent_response } }
//           { type: 'interruption' }              → barge-in: drop queued audio
//           { type: 'ping', ping_event: { event_id, ping_ms } } → must pong
//   client → { user_audio_chunk: <base64 pcm16> }
//           { type: 'pong', event_id }
//           { type: 'contextual_update', text }   → inject camera vision, etc.
//
// Audio: ConvAI default is 16 kHz PCM in/out. We resample mic → 16 kHz and play
// the 16 kHz output through a dedicated AudioContext. Mirrors the callback shape
// of OmniVoiceRealtimeClient so NexusVoiceSession can use either.
// =============================================================================

const INPUT_SAMPLE_RATE = 16000;
const DEFAULT_OUTPUT_SAMPLE_RATE = 16000;
const FRAME_SIZE = 4096;
// Last-resort runaway breaker only. ElevenLabs streams a whole turn's audio
// FASTER than realtime, so for a longer answer the queued-speech backlog
// (nextPlaybackTime - now) legitimately reaches the answer's full duration within
// a second or two. A low cap here fired MID-ANSWER and cancelled the still-queued
// middle — heard in prod as "pierwsze zdanie, ucięcie, ostatnie zdanie, środek
// pominięty" + nakładające się głosy. Stale-audio-over-next-turn is handled
// correctly by the `interruption` (barge-in) event, and sample-rate drift was
// fixed separately, so this only needs to catch an absurd runaway (minutes),
// never a real reply.
const MAX_PLAYBACK_BACKLOG_S = 180;

// --- Anti-self-trigger + barge-in gate ---------------------------------------
// Root cause of "przekrzykujemy się": assistant TTS plays through a SEPARATE
// AudioContext that the browser's getUserMedia echo-canceller has no reliable
// loopback reference for, so the assistant's own voice leaks into the mic and
// self-triggers ElevenLabs' server-side VAD → the agent thinks the user is
// talking and both voices overlap. While assistant audio is actively playing we
// therefore GATE outgoing mic frames (they are almost certainly our own echo)
// UNLESS the mic energy clearly exceeds a barge-in threshold — that is a genuine
// interruption, so we flush the queued assistant audio locally and let the frame
// through (the server VAD then also issues its own `interruption`).
//
// RMS gate is a runtime hypothesis (echo residual depends on device/AEC), so it
// is tuned conservatively AGAINST self-trigger: high enough that normal TTS echo
// (after echoCancellation) stays below it, low enough that real speech crosses
// it. The only behavior it can regress is barge-in itself (already broken per the
// symptom); normal turns are untouched because the assistant is not speaking when
// the user talks.
const BARGE_IN_RMS = 0.08;
// Consecutive above-threshold frames before we treat it as a real interruption
// (debounces coughs/clicks). At ~4096 samples/frame this is ~2 frames of speech.
const BARGE_IN_FRAMES = 2;
// Keep the gate active briefly after the last buffer ends so the tail of our own
// audio (and its room echo) cannot self-trigger in the silence right after.
const GATE_HANGOVER_MS = 250;

interface ElevenLabsClientOptions {
  signedUrl: string;
  callbacks: RealtimeClientCallbacks;
  connectTimeoutMs?: number;
  /** ConvAI dynamic variables (tenant_id/user_id) — substituted into the agent
   *  prompt so the NEXUS custom-LLM bridge knows whose data to use. */
  dynamicVariables?: Record<string, string>;
}

export class ElevenLabsRealtimeClient {
  private ws: WebSocket | null = null;
  private opts: ElevenLabsClientOptions;
  private state: RealtimeConnectionState = 'closed';

  private audioCtx: AudioContext | null = null;
  private captureCtx: AudioContext | null = null;
  private micStream: MediaStream | null = null;
  private processor: ScriptProcessorNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;

  private nextPlaybackTime = 0;
  private scheduled: AudioBufferSourceNode[] = [];
  private stopped = false;
  private outputSampleRate = DEFAULT_OUTPUT_SAMPLE_RATE;

  // Gate state (see BARGE_IN_* / GATE_HANGOVER_MS above).
  private lastAudioEndedAt = 0; // Date.now() when the queue last drained; 0 = not gating.
  private speechFramesDuringPlayback = 0;

  constructor(opts: ElevenLabsClientOptions) {
    this.opts = opts;
  }

  private setState(s: RealtimeConnectionState): void {
    if (this.state === s) return;
    this.state = s;
    this.opts.callbacks.onStateChange?.(s);
  }

  async connect(): Promise<void> {
    // Mic first: if the user denies it, fail before opening the socket.
    this.micStream = await navigator.mediaDevices.getUserMedia({
      // autoGainControl added (parity with the OpenAI relay client): together
      // with echo cancellation it keeps the assistant's own voice from being
      // boosted in the mic and self-triggering the server VAD.
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    const timeoutMs = this.opts.connectTimeoutMs ?? 8000;
    await new Promise<void>((resolve, reject) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new Error('ElevenLabs realtime connect timeout'));
      }, timeoutMs);

      const ws = new WebSocket(this.opts.signedUrl);
      this.ws = ws;
      this.setState('connecting');

      ws.onopen = () => {
        // Init with dynamic variables (tenant/user identity for the NEXUS
        // custom-LLM bridge); agent config defaults apply for the rest.
        ws.send(
          JSON.stringify({
            type: 'conversation_initiation_client_data',
            dynamic_variables: this.opts.dynamicVariables ?? {},
          })
        );
      };
      ws.onmessage = (ev) => {
        const first = this.handleMessage(ev.data);
        if (first && !settled) {
          settled = true;
          clearTimeout(timer);
          this.startCapture();
          this.setState('listening');
          resolve();
        }
      };
      ws.onerror = () => {
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          reject(new Error('ElevenLabs realtime socket error'));
        }
        this.opts.callbacks.onError?.(new Error('socket error'));
      };
      ws.onclose = (ev) => {
        this.opts.callbacks.onClose?.(ev.code, ev.reason);
        this.teardownAudio();
      };
    });
  }

  /** Returns true when this message is the init metadata (connect() resolver). */
  private handleMessage(raw: unknown): boolean {
    let msg: Record<string, unknown>;
    try {
      msg = JSON.parse(typeof raw === 'string' ? raw : '');
    } catch {
      return false;
    }
    const type = msg.type as string;

    switch (type) {
      case 'conversation_initiation_metadata': {
        // Honor the agent's actual output format (e.g. "pcm_16000"/"pcm_24000").
        // Decoding at the wrong rate distorts pitch AND duration (queue drift).
        const ev = msg.conversation_initiation_metadata_event as
          | { agent_output_audio_format?: string }
          | undefined;
        const m = /^pcm_(\d{4,6})$/.exec(ev?.agent_output_audio_format || '');
        if (m) this.outputSampleRate = parseInt(m[1], 10);
        return true;
      }
      case 'audio': {
        const ev = msg.audio_event as { audio_base_64?: string } | undefined;
        if (ev?.audio_base_64) {
          this.setState('speaking');
          this.enqueueAudio(ev.audio_base_64);
        }
        return false;
      }
      case 'user_transcript': {
        const ev = msg.user_transcription_event as { user_transcript?: string } | undefined;
        if (ev?.user_transcript) this.opts.callbacks.onUserTranscript?.(ev.user_transcript);
        return false;
      }
      case 'agent_response': {
        const ev = msg.agent_response_event as { agent_response?: string } | undefined;
        if (ev?.agent_response)
          this.opts.callbacks.onAssistantTranscript?.(ev.agent_response, true);
        return false;
      }
      case 'agent_response_correction': {
        // After a barge-in the server sends the truncated version of what was
        // actually spoken — keep the caption honest.
        const ev = msg.agent_response_correction_event as
          | { corrected_agent_response?: string }
          | undefined;
        if (ev?.corrected_agent_response)
          this.opts.callbacks.onAssistantTranscript?.(ev.corrected_agent_response, true);
        return false;
      }
      case 'interruption':
        // Barge-in: the user started talking — drop everything queued.
        this.cancelPlayback();
        this.setState('listening');
        return false;
      case 'ping': {
        const ev = msg.ping_event as { event_id?: number } | undefined;
        this.ws?.send(JSON.stringify({ type: 'pong', event_id: ev?.event_id }));
        return false;
      }
      default:
        return false;
    }
  }

  private startCapture(): void {
    if (!this.micStream) return;
    const captureCtx = new AudioContext();
    this.captureCtx = captureCtx;
    const source = captureCtx.createMediaStreamSource(this.micStream);
    this.sourceNode = source;
    const processor = captureCtx.createScriptProcessor(FRAME_SIZE, 1, 1);
    this.processor = processor;

    processor.onaudioprocess = (e) => {
      if (this.stopped || this.ws?.readyState !== WebSocket.OPEN) return;
      const input = e.inputBuffer.getChannelData(0);

      // Simple RMS for the orb level meter AND the self-trigger gate below.
      let sum = 0;
      for (let i = 0; i < input.length; i++) sum += input[i] * input[i];
      const rms = Math.sqrt(sum / input.length);
      this.opts.callbacks.onAudioLevel?.(Math.min(1, rms * 4));

      // Anti-self-trigger + barge-in: while our own audio is playing, only let
      // the mic through if it clearly crosses the barge-in threshold (a real
      // interruption); otherwise it is our TTS echo and streaming it would make
      // the agent talk over the user.
      if (this.isAssistantAudioActive()) {
        if (rms > BARGE_IN_RMS && ++this.speechFramesDuringPlayback >= BARGE_IN_FRAMES) {
          // Genuine barge-in: flush queued assistant audio so already-received
          // deltas stop playing, then fall through and stream this frame — the
          // server VAD will also register the interruption and stop generating.
          this.cancelPlayback();
          this.setState('listening');
          this.speechFramesDuringPlayback = 0;
        } else {
          if (rms <= BARGE_IN_RMS) this.speechFramesDuringPlayback = 0;
          return; // gate: suppress echo / still confirming a barge-in
        }
      }

      const resampled = resampleLinear(input, captureCtx.sampleRate, INPUT_SAMPLE_RATE);
      const pcm = floatToPcm16(resampled);
      const b64 = bytesToBase64(new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.byteLength));
      this.ws.send(JSON.stringify({ user_audio_chunk: b64 }));
    };

    source.connect(processor);
    processor.connect(captureCtx.destination);
  }

  private enqueueAudio(base64Pcm16: string): void {
    if (this.stopped) return;
    if (!this.audioCtx) this.audioCtx = new AudioContext({ sampleRate: this.outputSampleRate });
    const ctx = this.audioCtx;
    const float = decodePcm16Base64(base64Pcm16);
    if (float.length === 0) return;

    const buffer = ctx.createBuffer(1, float.length, this.outputSampleRate);
    buffer.getChannelData(0).set(float);
    const node = ctx.createBufferSource();
    node.buffer = buffer;
    node.connect(ctx.destination);

    const now = ctx.currentTime;
    // Backlog guard: if we've fallen far behind realtime, stale audio would
    // play over the next turn — flush and resync instead.
    if (this.nextPlaybackTime - now > MAX_PLAYBACK_BACKLOG_S) this.cancelPlayback();
    const startAt = Math.max(now, this.nextPlaybackTime);
    node.start(startAt);
    this.nextPlaybackTime = startAt + buffer.duration;
    this.scheduled.push(node);
    node.onended = () => {
      this.scheduled = this.scheduled.filter((n) => n !== node);
      if (this.scheduled.length === 0 && !this.stopped) {
        // Start the hangover window so the gate stays up briefly after our
        // audio ends (tail echo can still self-trigger in the silence).
        this.lastAudioEndedAt = Date.now();
        this.setState('listening');
      }
    };
  }

  /** True while our own audio is playing OR within the post-playback hangover. */
  private isAssistantAudioActive(): boolean {
    if (this.scheduled.length > 0) return true;
    if (this.lastAudioEndedAt === 0) return false;
    return Date.now() - this.lastAudioEndedAt < GATE_HANGOVER_MS;
  }

  private cancelPlayback(): void {
    for (const node of this.scheduled) {
      try {
        node.stop();
      } catch {
        /* already stopped */
      }
    }
    this.scheduled = [];
    this.nextPlaybackTime = this.audioCtx?.currentTime ?? 0;
    // Clear the hangover: after an explicit flush (barge-in / server
    // interruption) the mic must stream immediately so the user is heard.
    this.lastAudioEndedAt = 0;
    this.speechFramesDuringPlayback = 0;
  }

  /** Inject a live camera observation as conversation context (parity with OmniVoice). */
  sendVisionContext(description: string): void {
    if (this.ws?.readyState === WebSocket.OPEN && description.trim()) {
      this.ws.send(
        JSON.stringify({
          type: 'contextual_update',
          text: `Obserwacja z kamery użytkownika: ${description.trim()}`,
        })
      );
    }
  }

  private teardownAudio(): void {
    this.cancelPlayback();
    try {
      this.processor?.disconnect();
      this.sourceNode?.disconnect();
    } catch {
      /* noop */
    }
    this.micStream?.getTracks().forEach((t) => t.stop());
    void this.captureCtx?.close().catch(() => undefined);
    void this.audioCtx?.close().catch(() => undefined);
    this.processor = null;
    this.sourceNode = null;
    this.micStream = null;
    this.captureCtx = null;
    this.audioCtx = null;
  }

  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    this.setState('closed');
    try {
      this.ws?.close(1000, 'client stop');
    } catch {
      /* noop */
    }
    this.ws = null;
    this.teardownAudio();
  }
}
